import React from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Truck, X } from "lucide-react";

// Styled double-booking warning. Shown by the dispatch console BEFORE the SMS / home-time popups,
// driven by the backend pre-check (GET /fleet/loads/check-double-book). "Book anyway" carries the
// backend's conflict token into the write so only that exact conflict is overridden; the backend
// remains the authoritative enforcement.
export default function DoubleBookModal({ open, data, busy, onCancel, onConfirm }) {
  if (!open || !data) return null;
  const conflicts = data.conflicts || [];
  return createPortal(
    <div style={S.overlay} onMouseDown={onCancel}>
      <div style={S.card} onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button style={S.close} onClick={onCancel} title="Close" aria-label="Close"><X size={16} /></button>
        <div style={S.head}>
          <div style={S.badge}><AlertTriangle size={20} color="#b45309" /></div>
          <div>
            <div style={S.title}>Possible double-booking</div>
            <div style={S.sub}>This truck is already committed during that window</div>
          </div>
        </div>

        <div style={S.body}>
          <div style={S.truckRow}>
            <Truck size={15} color="var(--muted)" />
            <span>Truck <strong>{data.unit || "—"}</strong> already has {conflicts.length === 1 ? "a load" : `${conflicts.length} loads`} that overlap{conflicts.length === 1 ? "s" : ""}:</span>
          </div>
          <div style={S.conflicts}>
            {conflicts.map((c) => (
              <div key={c.load_id} style={S.conflict}>
                <span style={S.window}>{c.existing_pickup} → {c.existing_delivery}</span>
                <span style={S.tag}>{c.delivery_source === "live_eta" ? "live ETA" : "scheduled"}</span>
              </div>
            ))}
          </div>
          <p style={S.note}>A truck can’t run two loads at the same time. Book this anyway only if you know the current load will clear in time.</p>
        </div>

        <div style={S.actions}>
          <button style={S.cancel} onClick={onCancel} disabled={busy}>Cancel</button>
          <button style={{ ...S.confirm, ...(busy ? S.confirmBusy : {}) }} onClick={onConfirm} disabled={busy}>
            {busy ? "Booking…" : "Book anyway"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const S = {
  overlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)",
             display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000, padding: 16 },
  card: { position: "relative", width: "100%", maxWidth: 440, background: "var(--card,#fff)", color: "var(--ink,#0f172a)",
          border: "1px solid var(--line,#e5e7eb)", borderRadius: 16, boxShadow: "0 24px 60px rgba(0,0,0,0.3)", padding: "22px 22px 18px" },
  close: { position: "absolute", top: 12, right: 12, background: "none", border: "none", cursor: "pointer", color: "var(--muted,#64748b)", padding: 4 },
  head: { display: "flex", gap: 13, alignItems: "center", marginBottom: 14 },
  badge: { width: 42, height: 42, borderRadius: 12, flexShrink: 0, background: "#fffbeb", border: "1px solid #fde68a",
           display: "flex", alignItems: "center", justifyContent: "center" },
  title: { fontSize: 17, fontWeight: 800, letterSpacing: "-0.01em" },
  sub: { fontSize: 12.5, color: "var(--muted,#64748b)", marginTop: 2 },
  body: { fontSize: 13.5, lineHeight: 1.5 },
  truckRow: { display: "flex", alignItems: "center", gap: 7, color: "var(--ink,#0f172a)" },
  conflicts: { display: "flex", flexDirection: "column", gap: 6, margin: "10px 0 4px" },
  conflict: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
              background: "var(--bg,#f6f7f9)", border: "1px solid var(--line,#e5e7eb)", borderRadius: 9, padding: "8px 11px" },
  window: { fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  tag: { fontSize: 10.5, fontWeight: 700, color: "var(--muted,#64748b)", textTransform: "uppercase", letterSpacing: 0.4 },
  note: { fontSize: 12, color: "var(--muted,#64748b)", margin: "12px 0 0" },
  actions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 },
  cancel: { padding: "9px 16px", borderRadius: 10, border: "1px solid var(--line,#e5e7eb)", background: "var(--card,#fff)",
            color: "var(--ink,#0f172a)", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  confirm: { padding: "9px 18px", borderRadius: 10, border: "none", background: "#b45309", color: "#fff",
             fontSize: 13, fontWeight: 800, cursor: "pointer", boxShadow: "0 2px 10px rgba(180,83,9,0.35)" },
  confirmBusy: { opacity: 0.7, cursor: "default" },
};
